import { useEffect } from 'react';
import { ACTION_TYPES, useBiddingSearchDispatch, useBiddingSearchState } from './bidding-search-context';
import BiddingAppService from '../../../../application/platform/apm/bidding/bidding-app-service';
import { IBiddingListResponse } from '../../../../model/apm/bidding/response/bidding-list-response';
import { IBiddingAddEditRequest } from '../../../../model/apm/bidding/request/bidding-add-edit-request';
import { IBiddingPagedRequest } from '../../../../model/apm/bidding/request/bidding-paged-request';
import { IPortalDocumentResponse } from '../../../../model/apm/document/portal-document-response';
import { parseNumberInput } from '../../../../utils/format';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { useSearchActions } from '../../../../hooks/use-search-actions';
import { useDocumentAttachments } from '../../components/use-document-attachments';
import { attachmentsRemovalNotice } from '../../components/document-utils';
import { parseIdFilter } from '../../../../utils/filter-options';

export const useBiddingSearchController = () => {
    const dispatch = useBiddingSearchDispatch();
    const state = useBiddingSearchState();
    const { isSaving, runSaving, runWithLoading, run, showSuccess, confirmDelete } = useActionRunner();
    const { updateParameters, triggerSearch, ...searchHandlers } = useSearchActions<IBiddingPagedRequest>(
        dispatch, ACTION_TYPES, { term: undefined, year: undefined, modalityId: undefined, status: undefined },
    );
    const attachments = useDocumentAttachments({
        ownerId: state.drawer.bidding?.id,
        files: state.drawer.files,
        setFiles: (files: IPortalDocumentResponse[]) => dispatch({ type: ACTION_TYPES.setDrawerFiles, payload: files }),
        upload: BiddingAppService.uploadFile,
        remove: BiddingAppService.removeFile,
        onChange: triggerSearch,
    });

    useEffect(() => {
        run('Não foi possível carregar modalidades, unidades gestoras e tipos de documento', async () => {
            const [modalities, managementUnits, documentTypes] = await Promise.all([
                BiddingAppService.listModalities(), BiddingAppService.listManagementUnits(), BiddingAppService.listDocumentTypes(),
            ]);
            dispatch({ type: ACTION_TYPES.setLookups, payload: { modalities, managementUnits, documentTypes } });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-term') updateParameters({ term: value || undefined });
        if (name === 'input-year') updateParameters({ year: parseNumberInput(value) });
        if (name === 'select-modality') updateParameters({ modalityId: parseIdFilter(value) });
        if (name === 'select-status') updateParameters({ status: parseIdFilter(value) });
    }

    // A listagem não traz objeto e anexos, então a edição busca a licitação completa
    function openDrawer(row?: IBiddingListResponse) {
        if (!row) {
            dispatch({ type: ACTION_TYPES.openDrawer });
            return;
        }
        runWithLoading('Não foi possível carregar a licitação', async () => {
            dispatch({ type: ACTION_TYPES.openDrawer, payload: await BiddingAppService.getById(row.id) });
        });
    }

    function closeDrawer() {
        dispatch({ type: ACTION_TYPES.closeDrawer });
    }

    function handleSave(request: IBiddingAddEditRequest) {
        const editing = state.drawer.bidding;
        return runSaving('Não foi possível salvar a licitação', async () => {
            if (editing) {
                await BiddingAppService.edit(editing.id, request);
                showSuccess('Licitação atualizada com sucesso!');
                closeDrawer();
            } else {
                // Mantém o drawer aberto, agora em edição, para anexar os documentos
                dispatch({ type: ACTION_TYPES.openDrawer, payload: await BiddingAppService.add(request) });
                showSuccess('Licitação cadastrada! Agora anexe os documentos.');
            }
            triggerSearch();
        });
    }

    function handleDelete(row: IBiddingListResponse) {
        confirmDelete(`Tem certeza que deseja excluir a licitação "${row.number}"?${attachmentsRemovalNotice(row.fileCount)}`, () => run('Não foi possível excluir a licitação', async () => {
            await BiddingAppService.remove(row.id);
            showSuccess('Licitação excluída com sucesso!');
            triggerSearch();
        }));
    }

    return {
        isSaving,
        handleOnChange, ...searchHandlers,
        openDrawer, closeDrawer, handleSave, handleDelete, ...attachments,
    };
};
